// 入社・契約解除フォーム（公開）の API クライアント（docs/37）

import { noteAuthRequired } from "./authSignal";

export type EntryKind = "join" | "leave";
// applied = 送信と同時にマスタ登録済み（2026-08-28 以降）。他は承認フローがあった時期の記録
export type EntryStatus = "applied" | "pending" | "approved" | "rejected";

// アプリ内フォーム（PC版）と同じ項目を携帯からも入力できる（2026-08-28。docs/37）
export type EntryJoinInput = {
  name: string;
  org: string;
  branch: string;
  email: string;
  contractFrom: string;
  invoiceNo: string;
  unitTakkyu: number | null;
  unitNekopos: number | null;
  unitDm: number | null;
  monthlyKeitai: number | null;
  monthlySharyo: number | null;
  monthlyAppli: number | null;
  shidoCount: number | null;
  hasGasCard: boolean;
  contractDocOk: boolean;
  atStyle: boolean;
  notes: string;
};

export type EntryLeaveInput = { name: string; contractTo: string };

export type EntryRequest = {
  id: string;
  kind: EntryKind;
  status: EntryStatus;
  createdAt: string;
  submittedBy: string;
  join?: EntryJoinInput;
  leave?: EntryLeaveInput;
  handledAt?: string | null;
  handledBy?: string | null;
  note?: string | null;
  empId?: string | null;
};

async function postJson<T>(url: string, body: unknown): Promise<T | { ok: false; error: string }> {
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境では動きません）" };
    return (await r.json()) as T;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * 携帯の届出フォーム：送信＝そのままマスターシートへ登録（要ログイン。2026-08-28〜）。
 * 契約解除は empId（マスタの管理番号）で送る＝氏名の文字列では処理しない。
 */
export function submitEntry(input: {
  kind: EntryKind;
  join?: EntryJoinInput;
  leave?: EntryLeaveInput & { empId: string };
}): Promise<{ ok: boolean; empId?: string; name?: string; scheduled?: boolean; warnings?: string[]; error?: string }> {
  return postJson("/api/entry/submit", input) as Promise<{
    ok: boolean;
    empId?: string;
    name?: string;
    scheduled?: boolean;
    warnings?: string[];
    error?: string;
  }>;
}

/** 事務所：登録履歴（要ログイン） */
export async function fetchEntryRequests(): Promise<{
  ok: boolean;
  pendingCount?: number;
  requests?: EntryRequest[];
  error?: string;
}> {
  try {
    const r = await fetch("/api/entry/list");
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境）" };
    return (await r.json()) as { ok: boolean; pendingCount?: number; requests?: EntryRequest[]; error?: string };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * 契約解除の対象を選ぶための在籍者一覧（要ログイン）。
 * マスタ取得APIをそのまま使い、退職済みを除いて氏名順に返す。
 * ※ この画面はログイン必須なので、名簿が外部に出ることはない。
 */
export async function fetchActiveDrivers(): Promise<{ id: string; name: string; branch: string }[]> {
  try {
    const r = await fetch("/api/master");
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return [];
    const body = (await r.json()) as {
      ok?: boolean;
      drivers?: { id: string; name: string; branch: string; status?: string | null; contractTo?: string | null }[];
    };
    if (!body.ok || !body.drivers) return [];
    // 在籍判定は「状態」列を正とし、状態が空のときだけ契約終了日にフォールバック（driverStatus.ts と同じ）
    const active = body.drivers.filter((d) => (d.status ? d.status !== "退職" : !d.contractTo));
    return active
      .map((d) => ({ id: d.id, name: d.name, branch: d.branch }))
      .sort((a, b) => a.name.localeCompare(b.name, "ja"));
  } catch {
    return [];
  }
}
